/**
 * Ce que le chef de chantier voit de ses chantiers.
 *
 * Décidé avec le patron le 29 octobre 2026 : un « encadrant de terrain
 * élargi ». Sur les chantiers où il est affecté, il voit ce que voit le
 * conducteur — l'équipe du jour et ses pointages, qui vient les jours
 * suivants, ce que l'équipe fait remonter. Ailleurs, rien de plus qu'un
 * ouvrier.
 *
 * Trois projections étroites, calculées par la base (`site_team_today`,
 * `site_schedule`, `site_reports`) : le chef ne lit pas la fiche des salariés,
 * il lui faut un nom.
 */
import type { Role } from "../../context.js";
import type { DayPart, FieldReportKind, FieldReportSeverity, FieldReportStatus } from "../field/types.js";

/** Un compagnon affecté aujourd'hui, et son pointage sur ce chantier. */
export interface SiteTeamMember {
  employeeId: string;
  name: string;
  roleOnSite: string | null;
  dayPart: DayPart;
  arrivedAt: string | null;
  /** Vide tant qu'il est sur place, même s'il est déjà reparti puis revenu. */
  leftAt: string | null;
}

/** Une personne attendue un jour donné. */
export interface SiteScheduleEntry {
  date: string;
  employeeId: string;
  name: string;
  roleOnSite: string | null;
  dayPart: DayPart;
}

export interface SiteScheduleDay {
  date: string;
  people: SiteScheduleEntry[];
}

/** Une remontée du terrain, avancement compris, avec son auteur. */
export interface SiteReport {
  id: string;
  createdAt: string;
  kind: FieldReportKind;
  severity: FieldReportSeverity;
  status: FieldReportStatus;
  message: string;
  authorName: string | null;
  phaseName: string | null;
  declaredProgressPercent: number | null;
}

export interface SiteLeadView {
  team: SiteTeamMember[];
  schedule: SiteScheduleDay[];
  reports: SiteReport[];
}

/**
 * Les rôles qui encadrent un chantier. La base ajoute la seconde moitié de la
 * règle : le chef de chantier, sur ceux où il est affecté seulement.
 */
export const SITE_LEAD_ROLES: readonly Role[] = [
  "patron",
  "conducteur",
  "assistant",
  "lecture_seule",
  "chef_de_chantier",
];

/** Vrai pour le chef de chantier : c'est lui qui voit l'encadrement depuis « Ma journée ». */
export function leadsFromTheField(role: Role | null | undefined): boolean {
  return role === "chef_de_chantier";
}

export type PresenceState = "attendu" | "sur_place" | "reparti";

export function presenceOf(member: Pick<SiteTeamMember, "arrivedAt" | "leftAt">): PresenceState {
  if (!member.arrivedAt) return "attendu";
  return member.leftAt ? "reparti" : "sur_place";
}

/** Les jours dans l'ordre, et dans chaque jour les noms dans l'ordre. */
export function groupScheduleByDay(entries: readonly SiteScheduleEntry[]): SiteScheduleDay[] {
  const byDay = new Map<string, SiteScheduleEntry[]>();
  for (const entry of entries) {
    const day = byDay.get(entry.date);
    if (day) day.push(entry);
    else byDay.set(entry.date, [entry]);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, people]) => ({
      date,
      people: [...people].sort((a, b) => a.name.localeCompare(b.name, "fr")),
    }));
}
